import React from 'react';
import { Skeleton, Card, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReactComponent as UserIcon } from 'assets/svg/campaign-stats-user.svg';
import { ReactComponent as BookIcon } from 'assets/svg/book.svg';
import { ReactComponent as SubmissionIcon } from 'assets/svg/campaign-stats-submissions.svg';
import { ReactComponent as ActivityIcon } from 'assets/svg/activity.svg';

const { Title } = Typography;

interface CampaignStatsSectionProps {
  campaign: any;
  campaignStats: any;
  isLoading: boolean;
  hideTitleOnMobile?: boolean;
}

const getLearningHourText = (totalLearningTime: number, t: ReturnType<typeof useTranslation>['t']) => {
  if (totalLearningTime < 60) {
    return t('#Campaign.learning_minute', { count: totalLearningTime });
  } else {
    return t('#Campaign.learning_hour', { count: Math.floor(totalLearningTime / 60) });
  }
};

const CampaignStatsSection: React.FC<CampaignStatsSectionProps> = ({
  campaign,
  campaignStats,
  isLoading,
  hideTitleOnMobile = false,
}) => {
  const { t } = useTranslation();

  const getInteractionsCount = () => {
    if (!campaignStats) return 0;
    return (campaignStats.like_count || 0) + (campaignStats.comment_count || 0);
  };

  const statsData = [
    {
      key: 'joined_users',
      label: t('#Campaign.joined_users'),
      value: campaignStats?.joined_user_count || 0,
      icon: <UserIcon />,
      color: '#10b981',
    },
    {
      key: 'total_tasks',
      label: t('#Campaign.total_tasks'),
      value: campaign?.task_count || 0,
      icon: <BookIcon />,
      color: '#f59e0b',
    },
    {
      key: 'learning_hours',
      label: t('#Campaign.total_learning_hours'),
      value: getLearningHourText(campaign?.total_learning_time || 0, t),
      icon: <BookIcon />,
      color: '#8b5cf6',
    },
    {
      key: 'total_submissions',
      label: t('#Campaign.total_submissions'),
      value: campaignStats?.submission_count || 0,
      icon: <SubmissionIcon />,
      color: '#ef4444',
    },
    {
      key: 'interactions',
      label: t('#Campaign.interactions'),
      value: getInteractionsCount(),
      icon: <ActivityIcon />,
      color: '#d9d9d9',
    },
  ];

  return (
    <Card className={`sectionCard ${hideTitleOnMobile ? 'mobile-compact' : ''}`} bordered={false}>
      {!hideTitleOnMobile && (
        <Title level={4} className="statsTitle">
          {t('#Campaign.campaign_overview')}
        </Title>
      )}

      <div className="statsContainer">
        <div className="statsGrid">
          {statsData.map((stat) => (
            <div key={stat.key} className="statCard">
              <Skeleton loading={isLoading} paragraph={false} active>
                <div className="statCardContent">
                  <div
                    className="iconContainer"
                    style={{ background: `linear-gradient(135deg, ${stat.color}, ${stat.color}dd)` }}
                  >
                    {stat.icon}
                  </div>
                  <div className="statInfo">
                    <div className="statLabel">{stat.label}</div>
                    <div className="statValue">
                      {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                    </div>
                  </div>
                </div>
              </Skeleton>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default CampaignStatsSection;